import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Search, Check, Pencil } from "lucide-react";

/**
 * Single-select service combobox.
 * - Click to open a list of existing services
 * - Pick one to fill the value
 * - Type free-text in the search box → can pick "Use 'xyz' as custom"
 *
 * Props:
 *   services       : full services list
 *   value          : current string value
 *   onChange(str)  : called with the new value
 *   placeholder    : input placeholder text
 *   includeModifiers : default false — usually you want main services only
 *   className      : optional outer class
 */
export const ServiceCombobox = ({
  services = [],
  value = "",
  onChange,
  placeholder = "Pick a service or type custom...",
  includeModifiers = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  const list = useMemo(
    () => services.filter((s) => s.active && (includeModifiers || !s.is_modifier)),
    [services, includeModifiers]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) =>
      `${s.label || ""} ${s.description || ""}`.toLowerCase().includes(q)
    );
  }, [list, query]);

  const matchedExact = useMemo(
    () => list.some((s) => s.label.toLowerCase() === value.trim().toLowerCase()),
    [list, value]
  );

  const pick = (label) => {
    onChange(label);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="service-combobox-trigger"
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md border bg-white transition-colors overflow-hidden ${
          open ? "border-blue-800 ring-2 ring-blue-800/20" : "border-slate-300 hover:border-slate-400"
        }`}
      >
        <span className={`flex-1 min-w-0 truncate text-left ${value ? "text-slate-900" : "text-slate-400"}`}>
          {value || placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {value && !matchedExact && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
              <Pencil className="w-2.5 h-2.5" />
              Custom
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
          data-testid="service-combobox-dropdown"
        >
          <div className="p-2 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search or type a custom service..."
                data-testid="service-combobox-search"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && query.trim()) {
                    e.preventDefault();
                    pick(query.trim());
                  }
                }}
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:border-blue-800 focus:ring-2 focus:ring-blue-800/20"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filtered.length > 0 ? (
              <ul className="py-1">
                {filtered.map((s) => {
                  const selected = s.label === value;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => pick(s.label)}
                        data-testid={`service-combobox-row-${s.id}`}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                          selected ? "bg-blue-50" : "hover:bg-slate-50"
                        }`}
                      >
                        <div
                          className={`h-4 w-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                            selected ? "bg-slate-900 border-slate-900" : "border-slate-300 bg-white"
                          }`}
                        >
                          {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{s.label}</div>
                          {s.description && (
                            <div className="text-xs text-slate-500 truncate">{s.description}</div>
                          )}
                        </div>
                        {s.price_value > 0 && (
                          <span className="text-xs font-medium text-slate-500 flex-shrink-0">
                            ${s.price_value}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                {list.length === 0 ? "No saved services yet." : "No matches."}
              </div>
            )}
          </div>

          {query.trim() && !list.some((s) => s.label.toLowerCase() === query.trim().toLowerCase()) && (
            <div className="border-t border-slate-200 bg-slate-50 p-2">
              <button
                type="button"
                onClick={() => pick(query.trim())}
                data-testid="service-combobox-use-custom"
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-slate-900 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Use "{query.trim()}" as custom service
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceCombobox;
